import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config/apiConfig";

export const PATIENT_IMPORT_JOB_TYPES = {
  UPLOAD_PATIENT: "upload_patient",
  CHECK_ELIGIBILITY: "check_eligibility",
};

export const importIsActive = (status) => ["queued", "running", "stopping"].includes(status);
export const importCanStop = (status) => ["queued", "running"].includes(status);
export const importCanResume = (status) => ["failed", "paused"].includes(status);
export const importError = (error) => error?.response?.data?.error || error?.response?.data?.message || error?.message || "Request failed";

const LIST_POLL_INTERVAL = 5000;
const DETAIL_POLL_INTERVAL = 60 * 1000;
const LIST_PAGE_SIZE = 20;
const RESULTS_PAGE_SIZE = 20;

export const currentLocalDayRange = (now = new Date()) => {
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setDate(to.getDate() + 1);
  return { created_from: from.toISOString(), created_to: to.toISOString() };
};

export function authOptions(signal) {
  const token = (localStorage.getItem("token") || "").replace(/"/g, "");
  return { signal, timeout: 30000, headers: { Authorization: `Bearer ${token}` } };
}

function fetchImportJobs({ type, page = 1, createdFrom, createdTo, signal } = {}) {
  return axios
    .get(`${API_BASE_URL}/patient-imports`, {
      ...authOptions(signal),
      params: {
        type,
        page,
        page_size: LIST_PAGE_SIZE,
        created_from: createdFrom,
        created_to: createdTo,
      },
    })
    .then((response) => response.data);
}

function fetchImportJob(jobId, { type, signal } = {}) {
  return axios
    .get(`${API_BASE_URL}/patient-imports/${encodeURIComponent(jobId)}`, authOptions(signal))
    .then((response) => {
      const job = response.data;
      if (type && job?.type && job.type !== type) {
        throw new Error("This job belongs to a different import type and cannot be shown here.");
      }
      return job;
    });
}

function fetchImportResults(jobId, { afterId = 0, limit = RESULTS_PAGE_SIZE, errorsOnly = false, signal } = {}) {
  return axios
    .get(`${API_BASE_URL}/patient-imports/${encodeURIComponent(jobId)}/results`, {
      ...authOptions(signal),
      params: { after_id: afterId, limit, errors_only: errorsOnly },
    })
    .then((response) => response.data);
}

export async function uploadImportWorkbook(file, { type, signal, onProgress } = {}) {
  const body = new FormData();
  body.append("file", file);
  body.append("type", type);
  const { data } = await axios.post(`${API_BASE_URL}/uploadExcel`, body, {
    ...authOptions(signal),
    timeout: 0,
    onUploadProgress: onProgress,
  });
  return data;
}

export async function downloadImportIssues(jobId, fileName) {
  const response = await axios.get(`${API_BASE_URL}/patient-imports/${encodeURIComponent(jobId)}/issues.xlsx`, {
    ...authOptions(),
    responseType: "blob",
    timeout: 0,
  });
  saveBlob(response.data, fileName);
}

export async function downloadImportTemplate(fileName = "import-template.xlsx") {
  const response = await fetch(`${API_BASE_URL}/downloadExcel`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${(localStorage.getItem("token") || "").replace(/"/g, "")}`,
    },
  });
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  const blob = await response.blob();
  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = fileName;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1];
      if (!filename.endsWith(".xlsx")) filename += ".xlsx";
    }
  }
  saveBlob(blob, filename);
}

function saveBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function useImportJobs({ enabled = true, type } = {}) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!enabled || !type) return;
    setLoading(true);
    const controller = new AbortController();
    let timer;
    async function poll() {
      let keepPolling = true;
      try {
        const { created_from, created_to } = currentLocalDayRange();
        const data = await fetchImportJobs({
          type,
          page,
          createdFrom: created_from,
          createdTo: created_to,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setJobs(data.data || []);
        setTotalPages(data.total_pages || 1);
        setError("");
        keepPolling = (data.data || []).some((item) => importIsActive(item.status));
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(importError(e));
      }
      if (controller.signal.aborted) return;
      setLoading(false);
      if (keepPolling) timer = setTimeout(poll, LIST_POLL_INTERVAL);
    }
    // Deferred by one tick so React 18 StrictMode's mount/unmount/mount cycle
    // clears this timer in cleanup before the request is ever sent.
    timer = setTimeout(poll, 0);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [enabled, type, page, revision]);

  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  return { jobs, loading, page, setPage, totalPages, error, refresh };
}

export function useImportJobDetail(jobId, { enabled = true, type, issuesFileName } = {}) {
  const [job, setJob] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [cursors, setCursors] = useState([0]);
  const [nextCursor, setNextCursor] = useState(0);
  const [resultPages, setResultPages] = useState(1);
  const [resultHasMore, setResultHasMore] = useState(false);
  const [detailRevision, setDetailRevision] = useState(0);
  const [stopping, setStopping] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const cursor = cursors[cursors.length - 1];
  const resultsPage = cursors.length;
  const jobStatus = job?.status;
  const busy = stopping || resuming || downloading;

  const refresh = useCallback(() => setDetailRevision((value) => value + 1), []);

  const updateErrorsOnly = useCallback((value) => {
    setErrorsOnly(value);
    setCursors([0]);
  }, []);

  // Reset cursors and filters whenever the job or job type changes.
  useEffect(() => {
    setJob(null);
    setCursors([0]);
    setNextCursor(0);
    setErrorsOnly(false);
  }, [jobId, type]);

  useEffect(() => {
    if (!enabled || !jobId) return;
    if (jobStatus && !importIsActive(jobStatus)) return;
    setDetailError("");
    const controller = new AbortController();
    async function load() {
      setDetailLoading(true);
      try {
        const data = await fetchImportJob(jobId, { type, signal: controller.signal });
        if (controller.signal.aborted) return;
        setJob(data);
        setDetailError("");
      } catch (e) {
        if (controller.signal.aborted) return;
        setDetailError(importError(e));
      } finally {
        if (!controller.signal.aborted) setDetailLoading(false);
      }
    }
    const timer = setTimeout(load, 0);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [enabled, jobId, type, detailRevision, jobStatus]);

  useEffect(() => {
    if (!enabled || !jobId || !jobStatus) return;
    setResults([]);
    setResultsError("");
    const controller = new AbortController();
    async function load() {
      setResultsLoading(true);
      try {
        const data = await fetchImportResults(jobId, {
          afterId: cursor,
          limit: RESULTS_PAGE_SIZE,
          errorsOnly,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setResults(data.data || []);
        setNextCursor(data.next_after_id || 0);
        setResultPages(data.total_pages || 1);
        setResultHasMore(Boolean(data.has_more));
        setResultsError("");
      } catch (e) {
        if (controller.signal.aborted) return;
        setResultsError(importError(e));
      } finally {
        if (!controller.signal.aborted) setResultsLoading(false);
      }
    }
    const timer = setTimeout(load, 0);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [enabled, jobId, jobStatus, cursor, errorsOnly, detailRevision]);

  // While the job is still processing, count down to the next automatic refresh.
  useEffect(() => {
    if (!enabled || !importIsActive(jobStatus)) {
      setSecondsLeft(null);
      return;
    }
    const deadline = Date.now() + DETAIL_POLL_INTERVAL;
    setSecondsLeft(DETAIL_POLL_INTERVAL / 1000);
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) {
        clearInterval(interval);
        setDetailRevision((value) => value + 1);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [enabled, jobStatus, detailRevision]);

  const stop = useCallback(async () => {
    if (!jobId || !importCanStop(jobStatus)) return;
    setStopping(true);
    setDetailError("");
    try {
      const { data } = await axios.post(`${API_BASE_URL}/patient-imports/${encodeURIComponent(jobId)}/stop`, {}, authOptions());
      setJob((current) => (current ? { ...current, ...data } : data));
      setDetailRevision((value) => value + 1);
    } catch (e) {
      setDetailError(importError(e));
    } finally {
      setStopping(false);
    }
  }, [jobId, jobStatus]);

  const resume = useCallback(async () => {
    if (!jobId || !importCanResume(jobStatus)) return;
    setResuming(true);
    setDetailError("");
    try {
      const { data } = await axios.post(`${API_BASE_URL}/patient-imports/${encodeURIComponent(jobId)}/retry`, {}, authOptions());
      setJob((current) => (current ? { ...current, status: "queued", ...data } : current));
      setDetailRevision((value) => value + 1);
    } catch (e) {
      setDetailError(importError(e));
    } finally {
      setResuming(false);
    }
  }, [jobId, jobStatus]);

  const downloadIssues = useCallback(async () => {
    if (!job || Number(job.failed) < 1) return;
    setDownloading(true);
    setDetailError("");
    try {
      await downloadImportIssues(job.job_id, issuesFileName ? issuesFileName(job.job_id) : `import-issues-${job.job_id}.xlsx`);
    } catch (e) {
      setDetailError(importError(e));
    } finally {
      setDownloading(false);
    }
  }, [job, issuesFileName]);

  const changeResultsPage = useCallback(async (nextPage) => {
    if (nextPage === resultsPage || nextPage < 1 || nextPage > resultPages) return;
    if (nextPage < resultsPage) {
      setCursors((value) => value.slice(0, nextPage));
      return;
    }
    if (!resultHasMore) return;
    setResultsLoading(true);
    setResultsError("");
    const chain = [];
    let afterId = nextCursor;
    try {
      for (let step = resultsPage; step < nextPage; step++) {
        const data = await fetchImportResults(jobId, { afterId, limit: RESULTS_PAGE_SIZE, errorsOnly });
        chain.push(afterId);
        afterId = data.next_after_id || 0;
        if (!data.has_more) break;
      }
      setCursors((value) => [...value, ...chain]);
    } catch (e) {
      setResultsError(importError(e));
    } finally {
      setResultsLoading(false);
    }
  }, [jobId, errorsOnly, nextCursor, resultHasMore, resultPages, resultsPage]);

  return {
    job,
    jobStatus,
    detailLoading,
    detailError,
    results,
    resultsLoading,
    resultsError,
    errorsOnly,
    setErrorsOnly: updateErrorsOnly,
    resultsPage,
    resultPages,
    changeResultsPage,
    busy,
    stopping,
    resuming,
    downloading,
    secondsLeft,
    refresh,
    stop,
    resume,
    downloadIssues,
  };
}
